import {
  HStack,
  Image,
  ListItem,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  UnorderedList,
} from '@chakra-ui/react';
import useSubscriptionAddonsQuery from 'hooks/useSubscriptionAddonsQuery';
import useUnbilledChargeQuery from 'hooks/useUnbilledChargeQuery';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { get, isEmpty, isNil } from 'lodash';
import { useTranslation } from 'react-i18next';

import config from '../../config';
import { formatCurrency } from '../../helpers/formatCurrency';
import { displayRow } from '../../helpers/general';
import { CollapsibleContentCard } from '../../layout/CollapsibleContentCard';
import { ContentCard } from '../../layout/ContentCard';
import AddonsDetailsCard from './AddonsDetailsCard';
import SubscriptionChangesCard from './SubscriptionChangesCard';
import { UnbilledChargesTable } from './UnbilledChargesTable';

const SubscriptionDetailsCard = ({ workshopId }: { workshopId: number }) => {
  const { t } = useTranslation();

  const workshopQuery = useWorkshopQuery(workshopId);
  const workshopAddonQuery = useSubscriptionAddonsQuery(workshopId);
  const unbilledChargesQuery = useUnbilledChargeQuery(workshopId);

  const charges = Object.values(get(workshopQuery.data?.subscription, 'charges', {}));

  const quotas = get(workshopQuery.data?.subscription, `quotas`, {});

  return (
    <>
      <ContentCard
        isLoading={workshopQuery.isLoading || workshopAddonQuery.isLoading || unbilledChargesQuery.isLoading}
        icon={
          !isNil(workshopQuery.data?.cb_customer_id) ? (
            <a
              href={`${config.chargeBeeUrl}/d/customers/${workshopQuery.data?.cb_customer_id}`}
              data-test-id="open-in-chargebee-button"
              target="_blank"
              rel="noreferrer"
            >
              <Image src="/images/chargebee.png" alt="close" w={'50px'} />
            </a>
          ) : undefined
        }
        header={t('chargebee:subscription.label')}
        data-test-id="subscription-detail-card"
        minH="unset"
      >
        {!isEmpty(workshopQuery.data?.subscription) ? (
          <Stack w="full" spacing={2} alignSelf={'start'}>
            <Stack spacing={4}>
              <HStack>
                <Tooltip label={t('chargebee:subscription.plan.label')}>
                  <Tag variant={'outline'} size={'lg'} colorScheme={'blue'}>
                    <TagLabel>{t(`chargebee:plans.${get(workshopQuery.data?.subscription, 'plan.slug')}.name`)}</TagLabel>
                  </Tag>
                </Tooltip>
                <Tooltip label={t('chargebee:subscription.billing_period.label')}>
                  <Tag variant={'outline'} size={'lg'} colorScheme={'blue'}>
                    <TagLabel>{t(`chargebee:paymentIntervals.${get(workshopQuery.data?.subscription, 'billing_cycle')}`)}</TagLabel>
                  </Tag>
                </Tooltip>
              </HStack>
              {!workshopQuery.data?.subscription?.plan?.is_pay_per_use ? (
                <HStack spacing={12}>
                  <Stack>
                    <Stat>
                      <StatLabel>{t('service_record:label')}</StatLabel>
                      <StatNumber>
                        {(quotas as any)?.service_record?.free_quota_used}
                        {'/'}
                        {(quotas as any)?.service_record?.free_quota_granted}
                      </StatNumber>
                      <StatHelpText>{t('chargebee:quota.free_quota_granted.label')}</StatHelpText>
                    </Stat>
                  </Stack>
                  <Stack>
                    <Stat>
                      <StatLabel>{t('service_history_request:label')}</StatLabel>
                      <StatNumber>
                        {(quotas as any)?.service_history_request?.free_quota_used}
                        {'/'}
                        {(quotas as any)?.service_history_request?.free_quota_granted}
                      </StatNumber>
                      <StatHelpText>{t('chargebee:quota.free_quota_granted.label')}</StatHelpText>
                    </Stat>
                  </Stack>
                </HStack>
              ) : null}
            </Stack>
            <HStack justifyContent={'space-between'}>
              <Stack>
                {charges.length > 0 && (
                  <Stack spacing={1} mt={4}>
                    <Text fontSize="xs" fontWeight="semibold">
                      {t('chargebee:subscription.charges.title')}:
                    </Text>
                    <UnorderedList>
                      {charges.map((charge, index) => {
                        return (
                          <ListItem key={`charge-${index}`}>
                            {displayRow({
                              content:
                                //@ts-ignore
                                t(`chargebee:subscription.charges.${charge.id}`) +
                                '(' + //@ts-ignore
                                formatCurrency(charge?.charge) +
                                ')',
                              isLoading: unbilledChargesQuery.isFetching,
                            })}
                          </ListItem>
                        );
                      })}
                    </UnorderedList>
                  </Stack>
                )}
              </Stack>
            </HStack>
          </Stack>
        ) : (
          t('chargebee:subscription.empty.label')
        )}
        <SubscriptionChangesCard workshopId={workshopId} />
        <AddonsDetailsCard workshopId={workshopId} />
      </ContentCard>

      <CollapsibleContentCard
        isLoading={unbilledChargesQuery.isFetching}
        data-test-id="unbilled-charges-detail-card"
        minH="unset"
        header={t('chargebee:subscription.unbilled_charges.header')}
        contentDescription={`${t(
          'chargebee:subscription.unbilled_charges.label',
        )} (${formatCurrency(get(unbilledChargesQuery.data || {}, 'total', 0))})`}
      >
        <UnbilledChargesTable
          unbilledCharges={get(unbilledChargesQuery.data || {}, 'unbilled_charges', [])}
          isLoading={unbilledChargesQuery.isFetching}
        />
      </CollapsibleContentCard>
    </>
  );
};

export default SubscriptionDetailsCard;
